import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tree, TreeItem, TreeItemContent } from 'react-aria-components';
import type { Key } from '@react-types/shared';
import { api } from '../../../shared/api/apiClient';
import type { User, CategoryNodeDto, IntegrationCategoriesDto } from '../../../types';
import '../../../shared/styles/common-modal-styles.css';
import '../../../shared/styles/common-form-styles.css';
import '../../../shared/styles/common-button-styles.css';
import { buildTree } from '../../shared/categoryUtils';

interface IntegrationCategoriesProps {
    currentUser: User | null;
    selectionMode?: boolean;
    selectedCategoryIds?: number[];
    onCategorySelect?: (categoryId: number) => void;
    onCategoryDeselect?: (categoryId: number) => void;
    searchQuery?: string;
    hideEmptyCategories?: boolean;
    hideCheckedCategories?: boolean;
    getClientCategoryName?: (clientCategoryId: number) => string;
    selectedClientCategoryId?: number;
}

const categoryMatchesSearch = (category: CategoryNodeDto, searchQuery: string): boolean => {
    if (searchQuery.trim() === '') return true;
    const query = searchQuery.toLowerCase().trim();

    if (category.name.toLowerCase().includes(query)) return true;
    if (category.externalId && category.externalId.toLowerCase().includes(query)) return true;

    if (category.children && category.children.length > 0) {
        return category.children.some(child => categoryMatchesSearch(child, searchQuery));
    }

    return false;
};

const integrationMatchesSearch = (integration: IntegrationCategoriesDto, searchQuery: string): boolean => {
    if (searchQuery.trim() === '') return true;
    const query = searchQuery.toLowerCase().trim();

    if (integration.name.toLowerCase().includes(query)) return true;

    const checkCategories = (categories: CategoryNodeDto[]): boolean => {
        return categories.some(category => {
            if (category.name.toLowerCase().includes(query)) return true;
            if (category.externalId && category.externalId.toLowerCase().includes(query)) return true;
            if (category.children && category.children.length > 0) {
                return checkCategories(category.children);
            }
            return false;
        });
    };

    return checkCategories(integration.categories);
};

const IntegrationCategories: React.FC<IntegrationCategoriesProps> = ({
    selectionMode = false,
    selectedCategoryIds = [],
    onCategorySelect,
    onCategoryDeselect,
    searchQuery = '',
    hideEmptyCategories = false,
    hideCheckedCategories = false,
    getClientCategoryName,
    selectedClientCategoryId
}) => {
    const [allIntegrationsWithCategories, setAllIntegrationsWithCategories] = useState<IntegrationCategoriesDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState<Record<string, Set<string>>>(() => {
        const stored = localStorage.getItem('expandedCategoriesPerIntegration');
        if (stored) {
            const parsed = JSON.parse(stored);
            const restored: Record<string, Set<string>> = {};
            for (const [integrationId, keys] of Object.entries(parsed)) {
                restored[integrationId] = new Set(keys as string[]);
            }
            return restored;
        }
        return {};
    });
    const [expandedIntegrations, setExpandedIntegrations] = useState<Set<number>>(() => {
        const stored = localStorage.getItem('expandedIntegrationsInModal');
        if (stored) {
            try {
                return new Set(JSON.parse(stored));
            } catch {
                return new Set();
            }
        }
        return new Set();
    });

    const saveExpandedCategories = useCallback((expanded: Record<string, Set<string>>) => {
        const serialized = Object.entries(expanded).reduce((acc: Record<string, string[]>, [key, value]) => {
            acc[key] = [...value];
            return acc;
        }, {});
        localStorage.setItem('expandedCategoriesPerIntegration', JSON.stringify(serialized));
    }, []);

    const loadAllIntegrationsWithCategories = useCallback(async () => {
        try {
            setLoading(true);
            const integrationsWithCategoriesResult = await api.get<IntegrationCategoriesDto[]>('/api/categories');
            console.log('Loaded integrations with categories:', integrationsWithCategoriesResult.data);

            // Filter out integrations with no categories
            const filteredIntegrationsWithCategories = integrationsWithCategoriesResult.data.filter(integration => integration.categories.length > 0);

            buildTree(filteredIntegrationsWithCategories);
            
            console.log('Filtered integrations with categories:', filteredIntegrationsWithCategories);
            setAllIntegrationsWithCategories(filteredIntegrationsWithCategories);
        } catch (error) {
            console.error('Error loading integrations with categories:', error);
            setAllIntegrationsWithCategories([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAllIntegrationsWithCategories();
    }, [loadAllIntegrationsWithCategories]);

    useEffect(() => {
        localStorage.setItem('expandedIntegrationsInModal', JSON.stringify([...expandedIntegrations]));
    }, [expandedIntegrations]);

    const renderCategoryNode = ({
        node,
        selectedIntegration,
        selectionMode,
        selectedCategoryIds,
        onCategorySelect,
        onCategoryDeselect,
        searchQuery,
        hideEmptyCategories,
        hideCheckedCategories,
        expandedKeys,
        selectedClientCategoryId
    }: {
        node: CategoryNodeDto;
        selectedIntegration: string;
        selectionMode: boolean;
        selectedCategoryIds: number[];
        onCategorySelect?: (categoryId: number) => void;
        onCategoryDeselect?: (categoryId: number) => void;
        searchQuery: string;
        hideEmptyCategories: boolean;
        hideCheckedCategories: boolean;
        expandedKeys: Set<Key>;
        selectedClientCategoryId?: number;
    }): React.ReactNode => {
        const categoryKey = node.id.toString();
        const treeItemKey = `${selectedIntegration}:category_${categoryKey}`;
        const hasChildren = !!node.children?.length;
        const isExpanded = expandedKeys.has(treeItemKey);
        const isSelected = selectionMode && selectedCategoryIds.includes(node.id);

        const matchesSearch = categoryMatchesSearch(node, searchQuery);

        const handleToggle = () => {
            if (node.children && node.children.length > 0) {
                const newExpanded: Record<string, Set<string>> = { ...expandedCategories };
                if (!newExpanded[selectedIntegration]) {
                    newExpanded[selectedIntegration] = new Set();
                }
                if (newExpanded[selectedIntegration].has(categoryKey)) {
                    newExpanded[selectedIntegration].delete(categoryKey);
                } else {
                    newExpanded[selectedIntegration].add(categoryKey);
                }
                setExpandedCategories(newExpanded);
                saveExpandedCategories(newExpanded);
            }
        };

        const handleCheckboxChange = () => {
            if (selectionMode) {
                if (isSelected) {
                    onCategoryDeselect?.(node.id);
                } else {
                    onCategorySelect?.(node.id);
                }
            }
        };

        // Don't render if doesn't match search (but only if search query is not empty)
        if (searchQuery.trim() !== '' && !matchesSearch) {
            return null;
        }

        // Don't render if hide empty categories and this category has no products
        if (hideEmptyCategories && node.productCount === 0) {
            return null;
        }

        // Check if category is already mapped to client categories
        const isAlreadyMapped = node.mappedClientCategoryIds && node.mappedClientCategoryIds.trim() !== '';

        // Check if category is mapped to other client categories (excluding the current one)
        const isMappedToOtherCategories = isAlreadyMapped && selectedClientCategoryId !== undefined && (() => {
            const clientCategoryIds = node.mappedClientCategoryIds!.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            return clientCategoryIds.some(id => id !== selectedClientCategoryId);
        })();

        // Don't render if hide checked categories and this category is selected or mapped to other categories
        if (hideCheckedCategories && (isSelected || isMappedToOtherCategories)) {
            return null;
        }

        // Generate tooltip text with category paths (only for categories mapped to other categories)
        let tooltipText = '';
        if (isMappedToOtherCategories && getClientCategoryName) {
            const clientCategoryIds = node.mappedClientCategoryIds!.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            const categoryPaths = clientCategoryIds.map(id => getClientCategoryName!(id));
            tooltipText = 'Уже привязана к категориям\n' + categoryPaths.map(path => `- ${path}`).join('\n');
        }

        return (
            <TreeItem
                key={treeItemKey}
                id={treeItemKey}
                textValue={node.name}
                value={node}
                hasChildItems={hasChildren}
                className="category-tree-node"
            >
                <TreeItemContent>
                    {({ isExpanded: isReactAriaExpanded, hasChildItems }) => (
                        <div className={`category-tree-item ${hasChildItems ? 'expandable' : ''}`}>
                            {hasChildItems ? (
                                <Button slot="chevron" className="category-expand-icon category-expand-button">
                                    <span className={`category-tree-chevron ${isReactAriaExpanded ? 'expanded' : ''}`}>
                                        {'>'}
                                    </span>
                                </Button>
                            ) : (
                                <span className="category-expand-icon">
                                    &nbsp;
                                </span>
                            )}
                            <span className="category-name" onClick={handleToggle} style={{ cursor: 'pointer' }}>
                                {node.name}
                                {node.externalId && (
                                    <span className="category-id text-muted"> {node.externalId}</span>
                                )}
                                {isMappedToOtherCategories && (
                                    <span className="text-warning" title={tooltipText}>
                                        {' '}🔗
                                    </span>
                                )}
                            </span>
                            <span className="product-count text-muted">
                                [{node.productCount}]
                            </span>
                            {selectionMode && (
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={handleCheckboxChange}
                                    disabled={!!isMappedToOtherCategories}
                                    className="form-check-input"
                                    title={tooltipText}
                                />
                            )}
                            <span className="category-count">
                                ({node.children?.length || 0})
                            </span>
                        </div>
                    )}
                </TreeItemContent>
                {hasChildren && isExpanded && node.children!.map((child) => (
                    renderCategoryNode({
                        node: child,
                        selectedIntegration,
                        selectionMode,
                        selectedCategoryIds,
                        onCategorySelect,
                        onCategoryDeselect,
                        searchQuery,
                        hideEmptyCategories,
                        hideCheckedCategories,
                        expandedKeys,
                        selectedClientCategoryId
                    })
                ))}
            </TreeItem>
        );
    };

    const renderIntegrationNode = ({
        integration,
        setExpandedIntegrations,
        selectionMode,
        selectedCategoryIds,
        onCategorySelect,
        onCategoryDeselect,
        searchQuery,
        expandedKeys
    }: {
        integration: IntegrationCategoriesDto;
        setExpandedIntegrations: React.Dispatch<React.SetStateAction<Set<number>>>;
        selectionMode: boolean;
        selectedCategoryIds: number[];
        onCategorySelect?: (categoryId: number) => void;
        onCategoryDeselect?: (categoryId: number) => void;
        searchQuery: string;
        expandedKeys: Set<Key>;
    }): React.ReactNode => {
        const integrationKey = `integration_${integration.integrationId}`;
        const hasCategories = integration.categories.length > 0;
        const isExpanded = expandedKeys.has(integrationKey);

        const matchesSearch = integrationMatchesSearch(integration, searchQuery);

        if (searchQuery.trim() !== '' && !matchesSearch) {
            return null;
        }

        const handleToggle = () => {
            if (integration.categories.length > 0) {
                setExpandedIntegrations(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(integration.integrationId)) {
                        newSet.delete(integration.integrationId);
                    } else {
                        newSet.add(integration.integrationId);
                    }
                    return newSet;
                });
            }
        };

        return (
            <TreeItem
                key={integrationKey}
                id={integrationKey}
                textValue={integration.name}
                value={integration}
                hasChildItems={hasCategories}
                className="integration-tree-node"
            >
                <TreeItemContent>
                    {({ isExpanded: isReactAriaExpanded, hasChildItems }) => (
                        <div className={`integration-tree-item ${hasChildItems ? 'expandable' : ''}`}>
                            {hasChildItems ? (
                                <Button slot="chevron" className="integration-expand-icon integration-expand-button">
                                    <span className={`integration-tree-chevron ${isReactAriaExpanded ? 'expanded' : ''}`}>
                                        {'>'}
                                    </span>
                                </Button>
                            ) : (
                                <span className="integration-expand-icon">
                                    &nbsp;
                                </span>
                            )}
                            <span className="integration-name" onClick={handleToggle} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                {integration.name}
                            </span>
                            <span className="product-count text-muted">
                                [{integration.productCount}]
                            </span>
                            {selectionMode && (
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ visibility: 'hidden' }}
                                />
                            )}
                            <span className="category-count">
                                ({integration.categories.length})
                            </span>
                        </div>
                    )}
                </TreeItemContent>

                {hasCategories && isExpanded && integration.categories.map((category) => (
                    renderCategoryNode({
                        node: category,
                        selectedIntegration: integrationKey,
                        selectionMode,
                        selectedCategoryIds,
                        onCategorySelect,
                        onCategoryDeselect,
                        searchQuery,
                        hideEmptyCategories,
                        hideCheckedCategories,
                        expandedKeys,
                        selectedClientCategoryId
                    })
                ))}
            </TreeItem>
        );
    };

    const expandedKeys = React.useMemo(() => {
        const keys = new Set<Key>();
        const hasSearchQuery = searchQuery.trim() !== '';

        for (const integration of allIntegrationsWithCategories) {
            const integrationKey = `integration_${integration.integrationId}`;
            if (
                (hasSearchQuery && integrationMatchesSearch(integration, searchQuery))
                || expandedIntegrations.has(integration.integrationId)
            ) {
                keys.add(integrationKey);
            }

            const integrationCategoryKeys = expandedCategories[integrationKey] || new Set<string>();
            const visitCategories = (categories: CategoryNodeDto[]) => {
                for (const category of categories) {
                    const categoryKey = `${integrationKey}:category_${category.id}`;
                    if (
                        category.children?.length
                        && (
                            (hasSearchQuery && categoryMatchesSearch(category, searchQuery))
                            || integrationCategoryKeys.has(category.id.toString())
                        )
                    ) {
                        keys.add(categoryKey);
                    }

                    if (category.children?.length) {
                        visitCategories(category.children);
                    }
                }
            };

            visitCategories(integration.categories);
        }

        return keys;
    }, [allIntegrationsWithCategories, expandedCategories, expandedIntegrations, searchQuery]);

    const handleExpandedChange = useCallback((keys: 'all' | Set<Key>) => {
        if (keys === 'all') {
            return;
        }

        const nextIntegrations = new Set<number>();
        const nextCategories: Record<string, Set<string>> = {};

        for (const key of keys) {
            const keyText = String(key);
            const categoryMatch = keyText.match(/^(integration_\d+):category_(\d+)$/);
            if (categoryMatch) {
                const integrationKey = categoryMatch[1];
                const categoryId = categoryMatch[2];
                if (!nextCategories[integrationKey]) {
                    nextCategories[integrationKey] = new Set<string>();
                }
                nextCategories[integrationKey].add(categoryId);
                continue;
            }

            const integrationMatch = keyText.match(/^integration_(\d+)$/);
            if (integrationMatch) {
                nextIntegrations.add(Number(integrationMatch[1]));
            }
        }

        setExpandedIntegrations(nextIntegrations);
        setExpandedCategories(nextCategories);
        localStorage.setItem('expandedIntegrationsInModal', JSON.stringify([...nextIntegrations]));
        saveExpandedCategories(nextCategories);
    }, [saveExpandedCategories]);

    return (
        <div className="integration-categories container-fluid">
            {allIntegrationsWithCategories.length === 0 && !loading && (
                <div className="alert alert-info">
                    <h4>У вас нет доступных интеграций или они еще не загружены.</h4>
                </div>
            )}

            {allIntegrationsWithCategories.length > 0 && (
                <div className="unified-categories-section">
                    <Tree
                        aria-label="Категории поставщиков"
                        className="integration-categories-tree-unified"
                        expandedKeys={expandedKeys}
                        onExpandedChange={handleExpandedChange}
                    >
                        {allIntegrationsWithCategories.map((integration) => (
                            renderIntegrationNode({
                                integration,
                                setExpandedIntegrations,
                                selectionMode,
                                selectedCategoryIds,
                                onCategorySelect,
                                onCategoryDeselect,
                                searchQuery,
                                expandedKeys
                            })
                        ))}
                    </Tree>
                </div>
            )}
        </div>
    );
};

export default React.memo(IntegrationCategories);
